import * as React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Wind } from 'lucide-react';
import { motion } from 'framer-motion';

import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

import { chooseTarget, requiredCfm } from '@/lib/calculator';
import { canonical } from '@/lib/site';
import {
  ROOM_TYPES,
  PROBLEMS,
  getTopCombinations,
  type GuideCombination,
} from '@/data/seoVariables';

function GuideCard({ combo }: { combo: GuideCombination }) {
  const Icon = combo.problem.icon;
  const t = chooseTarget(combo.problem.concerns);
  const cadr = t ? Math.ceil(requiredCfm({ areaSqFt: combo.size, ceilingFt: combo.roomType.defaultCeilingHeight }, t.each) - 1e-9) : null;

  return (
    <Link to={combo.path} className="group">
      <Card className="h-full transition-all duration-200 hover:border-primary/50 hover:shadow-md">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm text-foreground">{combo.roomType.name}</span>
            <span className="text-xs text-muted-foreground">{combo.size} sq ft</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{combo.problem.name}</span>
          </div>
          <div className="flex items-center justify-between pt-1">
            <Badge variant="secondary" className="text-xs font-mono">
              CADR: {cadr} CFM
            </Badge>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function FilterChips({
  items,
  activeSlug,
  onSelect,
}: {
  items: { slug: string; name: string; icon?: React.ComponentType<{ className?: string }> }[];
  activeSlug: string;
  onSelect: (slug: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 mb-6">
      {items.map((item) => {
        const isActive = item.slug === activeSlug;
        const Icon = item.icon;
        return (
          <button
            key={item.slug}
            onClick={() => onSelect(item.slug)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border ${
              isActive
                ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                : 'bg-muted/40 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground'
            }`}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {item.name}
          </button>
        );
      })}
    </div>
  );
}

export default function GuidesIndex() {
  const allCombinations = getTopCombinations(200);
  const [activeConcern, setActiveConcern] = useState(PROBLEMS[4].slug); // Allergies
  const [activeRoom, setActiveRoom] = useState(ROOM_TYPES[0].slug); // Bedroom

  const concernFiltered = allCombinations.filter(c => c.problem.slug === activeConcern);
  const roomFiltered = allCombinations.filter(c => c.roomType.slug === activeRoom);

  const title = 'Air Purifier Buying Guides | CADR Calculator by Room Size & Type';
  const description = 'Browse our complete library of air purifier buying guides. Find the exact CADR rating you need for any room size, from bedrooms to basements, for allergies, pet dander, wildfire smoke, and more.';
  const canonicalUrl = canonical('/guides');

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: canonical('/') },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: canonicalUrl },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: canonicalUrl,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: allCombinations.slice(0, 10).map((combo, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `${combo.size} sq ft ${combo.roomType.name} for ${combo.problem.name}`,
        url: canonical(combo.path),
      })),
    },
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      </Helmet>

      <Header />

      <main className="min-h-screen bg-gradient-to-b from-background via-muted/20 to-background">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Guides</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Compact Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              Air Purifier Buying Guides
            </h1>
            <p className="text-muted-foreground">
              Find the right CADR for your room and concern.
            </p>
          </motion.div>

          {/* Tabbed Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Tabs defaultValue="concern" className="mb-12">
              <TabsList className="mb-6">
                <TabsTrigger value="concern">By Concern</TabsTrigger>
                <TabsTrigger value="room">By Room Type</TabsTrigger>
              </TabsList>

              <TabsContent value="concern">
                <FilterChips
                  items={PROBLEMS.map(p => ({ slug: p.slug, name: p.name, icon: p.icon }))}
                  activeSlug={activeConcern}
                  onSelect={setActiveConcern}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {concernFiltered.map(combo => (
                    <GuideCard key={combo.slug} combo={combo} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="room">
                <FilterChips
                  items={ROOM_TYPES.map(r => ({ slug: r.slug, name: r.name }))}
                  activeSlug={activeRoom}
                  onSelect={setActiveRoom}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {roomFiltered.map(combo => (
                    <GuideCard key={combo.slug} combo={combo} />
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>

          {/* CTA */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center py-12"
          >
            <Card className="bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">Need a Custom Calculation?</h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Our calculator lets you enter exact dimensions and select multiple air quality concerns for precise CADR recommendations.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  Try the Calculator
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </CardContent>
            </Card>
          </motion.section>
        </div>
      </main>

      <Footer />
    </>
  );
}
